"use client";

import React from "react";
import Link from "next/link";

export default function TermsPage() {
  return (
    <div className="min-h-screen py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-extrabold text-zinc-900 dark:text-zinc-100 mb-8">Terms and Conditions</h1>
        
        <div className="prose prose-zinc dark:prose-invert max-w-none">
          <p className="lead">
            Last updated: June 3, 2025
          </p>
          
          <h2>1. Agreement to Terms</h2>
          <p>
            These Terms and Conditions ("Terms") constitute a legally binding agreement made between you and Kled.io ("we," "us," or "our") concerning your access to and use of the Kled.io website and remote environment ecosystem services.
          </p>
          <p>
            By accessing or using our services, you agree to be bound by these Terms. If you disagree with any part of these Terms, you may not access or use our services.
          </p>
          
          <h2>2. Description of Services</h2>
          <p>
            Kled.io provides a remote environment ecosystem that enables end-to-end adoption of AI/ML into business processes, specifically designed for enterprises. Our services include but are not limited to:
          </p>
          <ul>
            <li>Remote development environments</li>
            <li>GitOps architecture implementation</li>
            <li>Kubernetes deployment and management</li>
            <li>AI/ML integration and workflow automation</li>
            <li>Secure data handling and processing</li>
            <li>Collaborative development tools</li>
          </ul>
          
          <h2>3. User Accounts</h2>
          <h3>3.1 Account Creation</h3>
          <p>
            To access certain features of our services, you must register for an account. When you register, you agree to provide accurate, current, and complete information about yourself as prompted by the registration form and to maintain and promptly update this information.
          </p>
          
          <h3>3.2 Account Responsibilities</h3>
          <p>
            You are responsible for:
          </p>
          <ul>
            <li>Maintaining the confidentiality of your account credentials</li>
            <li>All activities that occur under your account</li>
            <li>Notifying us immediately of any unauthorized use of your account</li>
          </ul>
          <p>
            We reserve the right to terminate your account if you violate these Terms or if we detect any fraudulent, abusive, or otherwise harmful activity.
          </p>
          
          <h2>4. Subscription and Payment</h2>
          <h3>4.1 Subscription Plans</h3>
          <p>
            We offer various subscription plans for our services. The features, limitations, and pricing of each plan are described on our website. We reserve the right to modify our subscription plans, including pricing and features, at any time.
          </p>
          
          <h3>4.2 Payment Terms</h3>
          <p>
            You agree to pay all fees associated with your subscription plan. Fees are payable in advance and are non-refundable except as expressly provided in these Terms. You authorize us to charge your designated payment method for all fees incurred.
          </p>
          
          <h3>4.3 Subscription Renewal</h3>
          <p>
            Subscriptions automatically renew at the end of each billing period unless you cancel your subscription before the renewal date. By subscribing to our services, you authorize us to charge your payment method for the renewal period.
          </p>
          
          <h2>5. Intellectual Property Rights</h2>
          <h3>5.1 Our Intellectual Property</h3>
          <p>
            The Kled.io platform, including its content, features, and functionality, is owned by us and protected by copyright, trademark, and other intellectual property laws. You may not copy, modify, distribute, sell, or lease any part of our services without our prior written consent.
          </p>
          
          <h3>5.2 Your Content</h3>
          <p>
            You retain all rights to any content you submit, post, or display on or through our services. By providing content to our services, you grant us a worldwide, non-exclusive, royalty-free license to use, reproduce, modify, adapt, publish, and display such content for the purpose of providing and improving our services.
          </p>
          
          <h2>6. Acceptable Use</h2>
          <p>
            You agree not to use our services:
          </p>
          <ul>
            <li>In any way that violates any applicable law or regulation</li>
            <li>To infringe upon the rights of others</li>
            <li>To distribute malware, viruses, or other harmful code</li>
            <li>To attempt to gain unauthorized access to our systems or networks</li>
            <li>To transmit spam, chain letters, or other unsolicited communications</li>
            <li>To interfere with or disrupt the integrity or performance of our services</li>
            <li>For any purpose that is unlawful, harmful, or otherwise objectionable</li>
          </ul>
          
          <h2>7. Data Privacy and Security</h2>
          <p>
            Your privacy is important to us. Our <Link href="/privacy-policy" className="text-emerald-600 hover:text-emerald-800 dark:text-emerald-400 dark:hover:text-emerald-300">Privacy Policy</Link> describes how we collect, use, and share your personal information when you use our services. By using our services, you agree to the collection and use of information in accordance with our Privacy Policy.
          </p>
          
          <h2>8. Limitation of Liability</h2>
          <p>
            To the maximum extent permitted by law, in no event shall Kled.io be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from:
          </p>
          <ul>
            <li>Your access to or use of or inability to access or use our services</li>
            <li>Any conduct or content of any third party on our services</li>
            <li>Any content obtained from our services</li>
            <li>Unauthorized access, use, or alteration of your transmissions or content</li>
          </ul>
          <p>
            Our total liability to you for any claim arising from or relating to these Terms or our services, regardless of the form of the action, shall not exceed the amount paid by you to us during the 12-month period preceding such claim.
          </p>
          
          <h2>9. Disclaimers</h2>
          <p>
            Our services are provided "as is" and "as available" without any warranties, either express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, non-infringement, or course of performance. We do not warrant that our services will function uninterrupted, secure, or available at any particular time or location, or that any errors or defects will be corrected.
          </p>
          
          <h2>10. Service Modifications and Termination</h2>
          <p>
            We reserve the right to modify, suspend, or discontinue our services, either temporarily or permanently, at any time without prior notice. We shall not be liable to you or any third party for any modification, suspension, or discontinuation of our services.
          </p>
          
          <h2>11. Indemnification</h2>
          <p>
            You agree to defend, indemnify, and hold harmless Kled.io and its officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, and expenses, including without limitation reasonable legal and accounting fees, arising out of or in any way connected with your access to or use of our services or your violation of these Terms.
          </p>
          
          <h2>12. Governing Law and Dispute Resolution</h2>
          <p>
            These Terms shall be governed by and construed in accordance with the laws of New South Wales, Australia, without regard to its conflict of law provisions. Any dispute arising from or relating to these Terms or our services shall be subject to the exclusive jurisdiction of the courts located in Sydney, Australia.
          </p>
          
          <h2>13. Changes to Terms</h2>
          <p>
            We reserve the right to modify these Terms at any time. We will provide notice of any material changes by posting the updated Terms on our website or through other communication channels. Your continued use of our services after such modifications constitute your acknowledgment and agreement to the modified Terms.
          </p>
          
          <h2>14. Contact Information</h2>
          <p>
            If you have any questions about these Terms, please contact us at:
          </p>
          <p>
            Kled.io<br />
            Email: legal@kled.io<br />
            Address: 101 Technology Drive, Sydney, NSW 2000, Australia
          </p>
        </div>
        
        <div className="mt-12 flex justify-center">
          <Link 
            href="/"
            className="inline-flex items-center justify-center rounded-md bg-emerald-500 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-emerald-600 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 transition-colors"
          >
            Return to Home
          </Link>
        </div>
      </div>
    </div>
  );
}
